import React, { useEffect, useRef } from 'react';
import { collection, onSnapshot } from 'firebase/firestore';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebase/config';
import { useAuth } from '../contexts/AuthContext';
import { playNotificationBeep } from '../utils/notificationSound';
import { theme } from '../theme';

/**
 * Company notification feed: toasts for new complains and complain status updates (branch or company/Guard app).
 * Skips the initial snapshot. Toast is clickable and opens the complains list with the detail modal.
 */
const CompanyNotificationListener: React.FC = () => {
  const { companyData } = useAuth();
  const companyId = companyData?.id;
  const navigate = useNavigate();
  const initialRef = useRef(true);

  useEffect(() => {
    if (!companyId) return undefined;

    const col = collection(db, 'notifications', 'companies', companyId);
    const unsub = onSnapshot(
      col,
      (snap) => {
        if (initialRef.current) {
          initialRef.current = false;
          return;
        }
        snap.docChanges().forEach((ch) => {
          if (ch.type !== 'added') return;
          const d = ch.doc.data() as {
            type?: string;
            title?: string;
            body?: string;
            complainId?: string;
            action?: string;
          };
          if (d.type !== 'complain') return;
          const action = d.action ?? 'created';

          playNotificationBeep();
          const title = d.title || (action === 'created' ? 'New complain' : 'Complain update');
          const body = d.body || '';
          const complainId = d.complainId || '';
          const href = complainId
            ? `/companies/${companyId}/complains?openComplain=${encodeURIComponent(complainId)}`
            : `/companies/${companyId}/complains`;

          toast.custom(
            (t) => (
              <button
                type="button"
                onClick={() => {
                  toast.dismiss(t.id);
                  navigate(href);
                }}
                style={{
                  margin: 0,
                  padding: `${theme.spacing[3]} ${theme.spacing[4]}`,
                  width: '100%',
                  maxWidth: 420,
                  textAlign: 'left',
                  cursor: 'pointer',
                  borderRadius: theme.borderRadius.md,
                  border: `1px solid ${theme.colors.light.border.medium}`,
                  background: theme.colors.white,
                  boxShadow: theme.shadows.lg,
                  fontFamily: theme.typography.fontFamily.primary,
                }}
              >
                <div
                  style={{
                    fontWeight: theme.typography.fontWeight.semibold,
                    marginBottom: theme.spacing[1],
                    color: '#000000',
                  }}
                >
                  {title}
                </div>
                {body ? (
                  <div style={{ fontSize: theme.typography.fontSize.sm, color: theme.colors.light.text.secondary }}>
                    {body}
                  </div>
                ) : null}
                <div
                  style={{
                    marginTop: theme.spacing[2],
                    fontSize: theme.typography.fontSize.xs,
                    color: theme.colors.primary[600],
                  }}
                >
                  Tap to open complains
                </div>
              </button>
            ),
            { duration: action === 'created' ? 9000 : 8000 }
          );
        });
      },
      (err) => console.error('Notification listener', err)
    );

    return () => {
      unsub();
      initialRef.current = true;
    };
  }, [companyId, navigate]);

  return null;
};

export default CompanyNotificationListener;
